// File: frontend/src/api/api.js

const API_BASE =
  process.env.REACT_APP_API_URL || "https://warfc.com/api";

/* ==========================================================
   PUBLIC ROUTE DETECTION
========================================================== */

/*
 * IMPORTANT:
 *
 * Do NOT mark entire namespaces such as "/clubs" or
 * "/tournaments" as public.
 *
 * A route can contain public GET endpoints while also
 * containing protected POST / PUT / DELETE endpoints.
 *
 * Protected requests must be allowed to trigger the
 * automatic access-token refresh.
 */

function isPublicRoute(path = "", method = "GET") {
  const cleanPath = String(path).split("?")[0];

  const normalizedMethod = String(method).toUpperCase();

  // --------------------------------------------------------
  // AUTH ROUTES
  // --------------------------------------------------------

  if (
    cleanPath === "/auth/login" ||
    cleanPath === "/auth/register" ||
    cleanPath === "/auth/refresh" ||
    cleanPath === "/auth/logout" ||
    cleanPath.startsWith("/auth/forgot-password") ||
    cleanPath.startsWith("/auth/reset-password")
  ) {
    return true;
  }

  // --------------------------------------------------------
  // PUBLIC FIGHTER REGISTRATION
  // --------------------------------------------------------

  if (
    cleanPath.startsWith("/fighters/register") &&
    normalizedMethod === "POST"
  ) {
    return true;
  }

  // --------------------------------------------------------
  // PUBLIC CLUB GET ROUTES
  // --------------------------------------------------------
  /*
   * GET /clubs
   * GET /clubs/:id
   * GET /clubs/:id/fighters
   *
   * But POST /clubs, PUT /clubs/:id,
   * POST /clubs/ranking, etc. remain protected.
   */

  if (
    cleanPath === "/clubs" ||
    cleanPath.startsWith("/clubs/")
  ) {
    return normalizedMethod === "GET";
  }

  // --------------------------------------------------------
  // PUBLIC TOURNAMENT ROUTES
  // --------------------------------------------------------

  if (
    cleanPath.startsWith("/tournaments/public") ||
    cleanPath.startsWith("/tournaments/active")
  ) {
    return normalizedMethod === "GET";
  }

  return false;
}

/* ==========================================================
   QUERY BUILDER
========================================================== */

function buildQuery(path, opts) {
  if (!opts || !opts.params) {
    return path;
  }

  const query = new URLSearchParams(
    opts.params
  ).toString();

  if (!query) {
    return path;
  }

  return `${path}?${query}`;
}

/* ==========================================================
   SAFE TOKEN GETTER
========================================================== */

function getToken() {
  const token =
    localStorage.getItem("userToken");

  if (
    !token ||
    token === "undefined" ||
    token === "null"
  ) {
    return null;
  }

  return token;
}

/* ==========================================================
   AUTH HEADER
========================================================== */

function applyAuthHeader(
  headers,
  path,
  method
) {
  const token = getToken();

  if (
    !isPublicRoute(path, method) &&
    token
  ) {
    headers["Authorization"] =
      `Bearer ${token}`;
  }

  return headers;
}

/* ==========================================================
   RESPONSE PARSER
========================================================== */

async function parseResponse(res) {
  let json = null;

  try {
    json = await res.json();
  } catch {
    json = null;
  }

  return {
    ok: res.ok,
    status: res.status,
    data: json,
  };
}

/* ==========================================================
   REFRESH ACCESS TOKEN
========================================================== */

/*
 * There must only ever be ONE refresh request running
 * at a time.
 *
 * Example:
 *
 * Request A -> 401
 * Request B -> 401
 * Request C -> 401
 *
 * A starts refresh.
 * B and C wait for the same refreshPromise.
 */

let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      console.log(
        "🔄 Refreshing WARFC access token..."
      );

      const res = await fetch(
        `${API_BASE}/auth/refresh`,
        {
          method: "POST",

          /*
           * Refresh token is stored in an
           * HttpOnly cookie.
           */
          credentials: "include",

          headers: {
            "Content-Type":
              "application/json",
          },
        }
      );

      const result =
        await parseResponse(res);

      if (
        !result.ok ||
        !result.data?.token
      ) {
        throw new Error(
          result.data?.message ||
            result.data?.error ||
            "Refresh token expired or invalid"
        );
      }

      const newToken =
        result.data.token;

      /*
       * Store the NEW 30-minute access token.
       */
      localStorage.setItem(
        "userToken",
        newToken
      );

      console.log(
        "✅ WARFC access token refreshed"
      );

      return newToken;

    } catch (err) {
      console.warn(
        "❌ Session refresh failed:",
        err.message
      );

      /*
       * At this point the refresh session itself
       * is no longer usable.
       */

      localStorage.removeItem(
        "userToken"
      );

      localStorage.removeItem(
        "userInfo"
      );

      return null;

    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/* ==========================================================
   UNIFIED REQUEST
========================================================== */

async function request(
  method,
  path,
  body = null,
  opts = {},
  isRetry = false
) {
  const url = buildQuery(
    path,
    opts
  );

  const normalizedMethod =
    String(method).toUpperCase();

  /*
   * Start with caller-provided headers.
   */
  const headers = {
    ...(opts.headers || {}),
  };

  /*
   * Add current access token.
   */
  applyAuthHeader(
    headers,
    url,
    normalizedMethod
  );

  const config = {
    method: normalizedMethod,
    headers,

    /*
     * Required for the HttpOnly refresh cookie.
     */
    credentials: "include",
  };

  /* ========================================================
     JSON BODY
  ======================================================== */

  if (
    body !== null &&
    body !== undefined &&
    !(body instanceof FormData)
  ) {
    /*
     * Don't overwrite caller's Content-Type if
     * they explicitly supplied one.
     */
    if (
      !headers["Content-Type"] &&
      !headers["content-type"]
    ) {
      headers["Content-Type"] =
        "application/json";
    }

    config.body =
      JSON.stringify(body);
  }

  /* ========================================================
     FORMDATA
  ======================================================== */

  if (
    body instanceof FormData
  ) {
    config.body = body;

    /*
     * DO NOT manually set multipart/form-data.
     *
     * Browser automatically creates:
     *
     * multipart/form-data; boundary=...
     */
  }

  /* ========================================================
     SEND REQUEST
  ======================================================== */

  let res;

  try {
    res = await fetch(
      `${API_BASE}${url}`,
      config
    );
  } catch (err) {
    console.error(
      "Network request failed:",
      err
    );

    throw new Error(
      "Network error. Please check your connection."
    );
  }

  const result =
    await parseResponse(res);

  /* ========================================================
     ACCESS TOKEN EXPIRED
  ======================================================== */

  if (
    result.status === 401 &&
    !isPublicRoute(
      url,
      normalizedMethod
    ) &&
    !isRetry
  ) {
    console.log(
      `🔐 401 received for ${normalizedMethod} ${url}`
    );

    /*
     * Get a fresh access token.
     *
     * If another request is already refreshing,
     * this waits for that same promise.
     */
    const newToken =
      await refreshAccessToken();

    if (newToken) {
      /*
       * Retry the ORIGINAL request exactly once.
       *
       * request() will read the new token from
       * localStorage and attach it automatically.
       */
      return request(
        normalizedMethod,
        path,
        body,
        opts,
        true
      );
    }

    /*
     * Refresh session has expired.
     */
    throw new Error(
      "Session expired. Please login again."
    );
  }

  /* ========================================================
     ERROR RESPONSE
  ======================================================== */

  if (!result.ok) {
    throw new Error(
      result.data?.error ||
        result.data?.message ||
        `Request failed ${result.status}`
    );
  }

  return result.data;
}

/* ==========================================================
   GET
========================================================== */

async function get(
  path,
  opts = {}
) {
  const json = await request(
    "GET",
    path,
    null,
    opts
  );

  /*
   * Existing WARFC weightclass convenience behavior.
   */
  const url = buildQuery(
    path,
    opts
  );

  if (
    url.includes("/weightclasses") &&
    Array.isArray(json)
  ) {
    const main =
      json.filter(
        (wc) =>
          wc.type === "main"
      );

    const altan_garaa =
      json.filter(
        (wc) =>
          wc.type !== "main"
      );

    return {
      main,
      altan_garaa,
    };
  }

  return json;
}

/* ==========================================================
   POST JSON
========================================================== */

async function postJSON(
  path,
  body = {},
  opts = {}
) {
  return request(
    "POST",
    path,
    body,
    opts
  );
}

/* ==========================================================
   POST FORMDATA
========================================================== */

async function postForm(
  path,
  formData,
  opts = {}
) {
  return request(
    "POST",
    path,
    formData,
    opts
  );
}

/* ==========================================================
   PUT JSON
========================================================== */

async function putJSON(
  path,
  body = {},
  opts = {}
) {
  return request(
    "PUT",
    path,
    body,
    opts
  );
}

/* ==========================================================

   PATCH JSON

========================================================== */

async function patchJSON(
  path,
  body = {},
  opts = {}
) {
  return request(
    "PATCH",
    path,
    body,
    opts
  );
}

/* ==========================================================
   PUT FORMDATA
========================================================== */

async function putForm(
  path,
  formData,
  opts = {}
) {
  return request(
    "PUT",
    path,
    formData,
    opts
  );
}

/* ==========================================================
   DELETE
========================================================== */

async function del(
  path,
  opts = {}
) {
  return request(
    "DELETE",
    path,
    null,
    opts
  );
}

/* ==========================================================
   EXPLICIT LOGOUT
========================================================== */

async function logout() {
  try {
    /*
     * Logout itself does not need the access token.
     *
     * The backend should clear the refresh cookie.
     */
    await fetch(
      `${API_BASE}/auth/logout`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type":
            "application/json",
        },
      }
    );
  } catch (err) {
    console.warn(
      "Logout request failed:",
      err
    );
  }

  localStorage.removeItem(
    "userToken"
  );

  localStorage.removeItem(
    "userInfo"
  );
}

/* ==========================================================
   EXPORT
========================================================== */

const api = {
  get,

  post: postJSON,
  postJSON,

  postForm,

  put: putJSON,
  putJSON,
  patch: patchJSON,

  putForm,

  delete: del,

  logout,
};

export default api;